import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { APP_THEME } from "../theme/theme";
import Header from "../components/header";
import { apiCall } from "../utils/api";
import toast, { Toaster } from 'react-hot-toast';

const filterTabs = ["All", "Recharge", "Bill Payment", "Add Money", "Bonus"];
const dateFilters = ["Today", "Yesterday", "Last 7 Days", "This Month", "Last Month", "All Time"];

function History() {
    const navigate = useNavigate();
    const [transactionHistory, setTransactionHistory] = useState([]);
    const [activeTab, setActiveTab] = useState("All");
    const [activeDateFilter, setActiveDateFilter] = useState("Last 7 Days");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchHistory();
    }, [activeTab, activeDateFilter]); // Re-fetch when tabs change

    const fetchHistory = async () => {
        setLoading(true);
        try {
            // Map UI filter values to API parameters
            let typeParam = "";
            if (activeTab === "Recharge") {
                typeParam = "recharge";
            } else if (activeTab === "Bill Payment") {
                typeParam = "bill";
            } else if (activeTab === "Add Money") {
                typeParam = "addmoney";
            } else if (activeTab === "Bonus") {
                typeParam = "bonus";
            } else {
                typeParam = "all";
            }

            // Map date filters
            let rangeParam = "";
            if (activeDateFilter === "Today") {
                rangeParam = "today";
            } else if (activeDateFilter === "Yesterday") {
                rangeParam = "yesterday";
            } else if (activeDateFilter === "Last 7 Days") {
                rangeParam = "last7days";
            } else if (activeDateFilter === "This Month") {
                rangeParam = "thismonth";
            } else if (activeDateFilter === "Last Month") {
                rangeParam = "lastmonth";
            } else {
                rangeParam = "all";
            }

            const result = await apiCall("history", {
                type: typeParam,
                range: rangeParam
            }, true);

            if (result.status === true && result.history) {
                setTransactionHistory(result.history);
            } else {
                toast.error(result.message || "An error occurred. Please try again.");
                setTransactionHistory([]);
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
            setTransactionHistory([]);
        } finally {
            setLoading(false);
        }
    };

    const getStatusDisplay = (status) => {
        // status: 0 = pending, 1 = success, 3 = failed
        switch (status) {
            case "1":
                return { bg: APP_THEME.successColor, label: "Success", color: "#FFFFFF" };
            case "2":
                return { bg: APP_THEME.errorColor, label: "Failed", color: "#FFFFFF" };
            case "0":
                return { bg: APP_THEME.warningColor, label: "Pending", color: "#FFFFFF" };
            case "3":
                return { bg: APP_THEME.warningColor, label: "Pending", color: "#FFFFFF" };
            default:
                return { bg: APP_THEME.textSecondary, label: "Unknown", color: "#FFFFFF" };
        }
    };

    const filterByDate = (transactions) => {
        // Client-side filtering as backup, but API should handle this
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        return transactions.filter((txn) => {
            const txnDate = new Date(txn.date);

            if (activeDateFilter === "Today") {
                return txnDate.toDateString() === today.toDateString();
            } else if (activeDateFilter === "Yesterday") {
                return txnDate.toDateString() === yesterday.toDateString();
            } else if (activeDateFilter === "Last 7 Days") {
                const weekAgo = new Date();
                weekAgo.setDate(weekAgo.getDate() - 7);
                return txnDate >= weekAgo;
            } else if (activeDateFilter === "This Month") {
                return txnDate.getMonth() === today.getMonth() && txnDate.getFullYear() === today.getFullYear();
            } else if (activeDateFilter === "Last Month") {
                const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
                return txnDate >= lastMonth && txnDate <= lastMonthEnd;
            }
            return true;
        });
    };

    const filterByType = (transactions) => {
        // Client-side filtering as backup
        if (activeTab === "All") return transactions;

        switch (activeTab) {
            case "Recharge":
                return transactions.filter(txn => txn.order_type === "2");
            default:
                return transactions;
        }
    };

    // Apply client-side filters as backup in case API doesn't filter properly
    const filtered = filterByType(filterByDate(transactionHistory));

    const groupByDate = (txns) => {
    const groups = {};
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    txns.forEach((txn) => {
        const d = new Date(txn.date);

        let label;

        if (d.toDateString() === today.toDateString()) {
            label = "Today";
        } else if (d.toDateString() === yesterday.toDateString()) {
            label = "Yesterday";
        } else {
            label = d.toLocaleDateString("en-IN", {
                day: "numeric",
                month: "short",
                year: "numeric"
            });
        }

        if (!groups[label]) {
            groups[label] = [];
        }

        groups[label].push(txn);
    });

    // IMPORTANT:
    // Sort transactions inside each date group by TIME DESCENDING
    Object.keys(groups).forEach((key) => {
        groups[key].sort((a, b) => {
            return new Date(b.time) - new Date(a.time);
        });
    });

    // Sort date groups newest first
    const sortedGroups = {};

    Object.keys(groups)
        .sort((a, b) => {
            if (a === "Today") return -1;
            if (b === "Today") return 1;

            if (a === "Yesterday") return -1;
            if (b === "Yesterday") return 1;

            return new Date(b) - new Date(a);
        })
        .forEach((key) => {
            sortedGroups[key] = groups[key];
        });

    return sortedGroups;
};

    const grouped = groupByDate(filtered);

    const formatTime = (dateStr) => {
        const d = new Date(dateStr);
        return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
    };

    const formatAmount = (amount) => {
        const amt = parseFloat(amount);
        return isNaN(amt) ? 0 : amt.toFixed(2);
    };

    // Helper function to truncate biller name
    const truncateBillerName = (name) => {
        if (!name) return "";
        return name.length > 10 ? name.substring(0, 10) + "..." : name;
    };

    return (
        <>
            <Header title={"Report"} showBack={true} />
            <div className="px-4 pb-4">
                {/* Date Filter Tabs */}
                <div className="overflow-x-auto scrollbar-hide pt-4 mb-1">
                    <div className="flex gap-2.5 min-w-max">
                        {dateFilters.map((df) => (
                            <button
                                key={df}
                                onClick={() => setActiveDateFilter(df)}
                                disabled={loading}
                                className="px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{
                                    border: `1.5px solid ${activeDateFilter === df ? APP_THEME.primaryColor : APP_THEME.borderColor}`,
                                    background: activeDateFilter === df ? `${APP_THEME.primaryColor}10` : "transparent",
                                    color: activeDateFilter === df ? APP_THEME.primaryColor : APP_THEME.textSecondary,
                                    fontFamily: APP_THEME.fontFamily,
                                }}
                            >
                                {df}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Type Filter Tabs */}
                <div className="overflow-x-auto scrollbar-hide py-3 mb-2">
                    <div className="flex gap-2.5 min-w-max">
                        {filterTabs.map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                disabled={loading}
                                className="px-4 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all duration-200 flex-shrink-0 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{
                                    border: `1px solid ${activeTab === tab ? APP_THEME.primaryColor : APP_THEME.borderColor}`,
                                    background: activeTab === tab ? APP_THEME.primaryColor : APP_THEME.bgColor,
                                    color: activeTab === tab ? APP_THEME.textOnPrimary : APP_THEME.textSecondary,
                                    fontFamily: APP_THEME.fontFamily,
                                }}
                            >
                                {tab}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Loading State */}
                {loading ? (
                    <div className="flex items-center justify-center min-h-[60vh]">
                        <div className="flex flex-col items-center gap-3">
                            <div
                                className="w-12 h-12 rounded-full border-4 border-t-transparent animate-spin"
                                style={{
                                    borderColor: `${APP_THEME.primaryColor}33`,
                                    borderTopColor: APP_THEME.primaryColor,
                                }}
                            />
                            <div className="text-sm font-medium" style={{ color: APP_THEME.textPrimary }}>
                                Loading transactions...
                            </div>
                        </div>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-16">
                        <div
                            className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                            style={{ background: APP_THEME.surfaceColor }}
                        >
                            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.textSecondary} strokeWidth="1.5">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                            </svg>
                        </div>
                        <p className="text-base font-semibold mb-1" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>
                            No transactions found
                        </p>
                        <p className="text-xs" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>
                            No transactions match your filters
                        </p>
                    </div>
                ) : (
                    Object.entries(grouped).map(([dateLabel, txns]) => (
                        <div key={dateLabel}>
                            <div
                                className="text-xs font-semibold uppercase my-4"
                                style={{
                                    color: APP_THEME.textSecondary,
                                    fontFamily: APP_THEME.fontFamily,
                                    letterSpacing: "0.5px",
                                }}
                            >
                                {dateLabel}
                            </div>
                            {txns.map((txn) => {
                                const statusDisplay = getStatusDisplay(txn.status);
                                const amount = formatAmount(txn.amount);
                                const truncatedName = txn.biller_name
                                    ? truncateBillerName(txn.biller_name)
                                    : txn.order_type;


                                return (
                                    <div
                                        key={txn.id}
                                        onClick={() => navigate("/transaction-detail", { state: { txn } })}
                                        className="relative flex items-center gap-3 p-4 rounded-xl border cursor-pointer mb-3 overflow-hidden transition-transform duration-100 active:scale-98"
                                        style={{
                                            background: APP_THEME.surfaceColor,
                                            borderColor: APP_THEME.borderColor,
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.02)",
                                        }}
                                    >
                                        {/* Logo */}
                                        <div
                                            className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden"
                                            style={{ background: APP_THEME.primaryLight }}
                                        >
                                            {txn.logo ? (
                                                <img
                                                    src={txn.logo}
                                                    alt={txn.biller_name}
                                                    className="w-8 h-8 object-contain"
                                                />
                                            ) : (
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={APP_THEME.primaryColor} strokeWidth="2">
                                                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                                                    <line x1="12" y1="18" x2="12.01" y2="18" />
                                                </svg>
                                            )}
                                        </div>

                                        {/* Details */}
                                        <div className="flex-1 min-w-0">
                                            <div className="text-xs font-bold" style={{ color: APP_THEME.textPrimary, fontFamily: APP_THEME.fontFamily }}>
                                                {truncatedName} · {formatTime(txn.time)}
                                            </div>
                                            <div className="text-xs mt-0.5 opacity-80" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>
                                                {txn.cn}
                                            </div>
                                        </div>

                                        {/* Amount */}
                                        <div
                                            className="text-sm font-extrabold flex-shrink-0"
                                            style={{
                                                color: APP_THEME.textPrimary,
                                                fontFamily: APP_THEME.fontFamily,
                                            }}
                                        >
                                            ₹{amount}
                                        </div>

                                        {/* Status Badge */}
                                        <div
                                            className="absolute top-0 right-0 text-[9px] font-extrabold uppercase py-1 px-2.5"
                                            style={{
                                                color: statusDisplay.color,
                                                background: statusDisplay.bg,
                                                borderBottomLeftRadius: "12px",
                                                borderTopRightRadius: "12px",
                                                fontFamily: APP_THEME.fontFamily,
                                                letterSpacing: "0.5px",
                                            }}
                                        >
                                            {statusDisplay.label}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))
                )}
            </div>
            <Toaster position="bottom-center" />

            <style>
                {`
                    .active\\:scale-98:active {
                        transform: scale(0.98);
                    }
                    .active\\:scale-95:active {
                        transform: scale(0.95);
                    }
                    .scrollbar-hide::-webkit-scrollbar {
                        display: none;
                    }
                    .scrollbar-hide {
                        -ms-overflow-style: none;
                        scrollbar-width: none;
                    }
                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                    .animate-spin {
                        animation: spin 0.8s linear infinite;
                    }
                    .truncate {
                        overflow: hidden;
                        text-overflow: ellipsis;
                        white-space: nowrap;
                    }
                `}
            </style>
        </>
    );
}

export default History;